import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomerReadDTO } from '../generated/api-client';
import { useCustomers } from '../hooks';
import { ReadOnlyDataGrid, ReadOnlyField, RowAction } from '../components/ReadOnlyDataGrid';
import { PageLayout } from '../components/layout';
import { ActionButton } from '../components/common';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useDeleteCustomer } from '../hooks/useCustomers';
import { ConfirmDialog } from '../components/common/ConfirmDialog';

export const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: customers = [], isLoading, error } = useCustomers();
  const deleteMutation = useDeleteCustomer();
  const [deleteState, setDeleteState] = React.useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  const fields: ReadOnlyField[] = [
    {
      field: 'company_name',
      headerName: 'Ragione sociale',
      width: 250,
      renderCell: (params: any) =>
        [params.row.company_name, params.row.company_name_type].filter(Boolean).join(' ') || '—',
    },
    {
      field: 'vat_number',
      headerName: 'Partita IVA',
      width: 200,
    },
    {
      field: 'tax_code',
      headerName: 'Codice fiscale',
      width: 200,
    },
    {
      field: 'ateco_2025',
      headerName: 'ATECO 2025',
      width: 150,
    },
    {
      field: 'last_update',
      headerName: 'Ultima modifica',
      width: 200,
      type: 'dateTime',
    },
  ];

  const actions: RowAction[] = [
    {
      label: 'Apri',
      icon: <EditIcon />,
      onClick: (id, row) => {
        navigate(`/customers/${id}`);
      },
    },
    {
      label: 'Elimina',
      icon: <DeleteIcon />,
      onClick: id => {
        setDeleteState({ open: true, id: String(id) });
      },
    },
  ];

  const handleAddCustomer = () => {
    navigate('/customers/new');
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Errore nel caricamento dei customers</Typography>
      </Box>
    );
  }

  return (
    <>
      <PageLayout
        header={
          <>
            <BusinessIcon fontSize="large" />
            <Typography variant="h5" sx={{ marginLeft: '1rem' }}>
              Clienti
            </Typography>
          </>
        }
        actions={
          <ActionButton
            icon={<AddIcon />}
            tooltip="Aggiungi nuovo customer"
            onClick={handleAddCustomer}
          />
        }
      >
        <Box sx={{ p: 2 }}>
          <ReadOnlyDataGrid
            fields={fields}
            data={customers}
            actions={actions}
            loading={isLoading}
            getId={row => row.id || ''}
            isFiltrable
          />
        </Box>
      </PageLayout>
      <ConfirmDialog
        open={deleteState.open}
        title="Conferma eliminazione"
        content="Sei sicuro di voler eliminare questo cliente?"
        confirmText="Elimina"
        cancelText="Annulla"
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteState({ open: false, id: null })}
        onConfirm={() => {
          if (deleteState.id) {
            deleteMutation.mutate(deleteState.id, {
              onSettled: () => setDeleteState({ open: false, id: null }),
            });
          }
        }}
      />
    </>
  );
};
